import React, { useState, useEffect, Children, useMemo, Fragment } from 'react';

import './DataTable.css';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TablePagination from '@mui/material/TablePagination';
import * as csvexporter from '../misc/CsvExporter.tsx';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MenuIcon from '@mui/icons-material/Menu';
import { FilterChips } from './FilterChips.tsx';
import { FilterDialog } from './FilterChips.tsx';
import { TextField } from '@mui/material';


export function DataTable({ showExport, status, keyname, columns, data, defaultSortColumn, errorHandler, getTokenFuncAsync, children }: {
    showExport?: boolean,
    status?: any,
    keyname: any,
    columns: any,
    data: any,
    defaultSortColumn: any,
    errorHandler?: any,
    getTokenFuncAsync: any,
    children?: any
}) {

    const [open, setOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState({});
    const [sortColumnn, setSortColumn] = useState(defaultSortColumn);
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterColumn, setFilterColumn] = useState<string | null>(null);
    const [filterValue, setFilterValue] = useState(null);
    const [searchValue, setSearchValue] = useState<string | null>(null);
    const [currentItemNumber, setCurrentItemNumber] = useState(0);
    const [sortedAndFilteredData, setSortedAndFilteredData] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [currentPageData, setCurrentPageData] = useState([]);
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [filteringColumns, setFilteringColumns] = useState({});
    const [tempFilteringColumns, setTempFilteringColumns] = useState({});


    useEffect(() => {
        const newData = sortAndFilter(status, searchValue, columns, data, sortColumnn, sortOrder, filteringColumns);
        setSortedAndFilteredData(newData);

    }, [data, searchValue, status, sortColumnn, sortOrder, filterValue, filteringColumns]);

    useEffect(() => {
        const pageOfData = getCurrentPageData(sortedAndFilteredData);
        setCurrentPageData(pageOfData);

    }, [sortedAndFilteredData, currentItemNumber, pageSize]);

    const pageNum = Math.floor(currentItemNumber / pageSize);

    const handleChangePage = (event, newPage) => {
        setCurrentItemNumber(newPage * pageSize);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setCurrentItemNumber(0);
    };

    const addFilter = (value) => {
        if (filterColumn) {
            var tempFilteringColumnsCopy = { ...tempFilteringColumns };
            tempFilteringColumnsCopy[filterColumn] = value;

            setFilterValue(value);
            setTempFilteringColumns(tempFilteringColumnsCopy);
        }
    }


    function getCurrentPageData(dataToSlice) {
        if (!dataToSlice) {
            return [];
        }
        return dataToSlice.slice(currentItemNumber, currentItemNumber + pageSize);
    }

    const handleActionClick = async (entryId, row_data, info) => {
        const selectedRow = data.find(row => row[keyname] === entryId);  //TODO: make more efficient


        if (info.perform_action) {

            try {
                var token = await getTokenFuncAsync();
                const altered_record = await info.perform_action(selectedRow, token);


                if (altered_record) {
                    const dataCopy = [...sortedAndFilteredData];
                    const updatedData = dataCopy.map(row => {
                        if (row['id'] === row_data['id']) {
                            return altered_record;
                        }
                        return row;
                    });

                    setSortedAndFilteredData(sortAndFilter(status, columns, updatedData, sortColumnn, sortOrder, null, null)); //TODO: pass the correct values
                }
            } catch (error) {
                console.log(error);

                if (errorHandler) {
                    errorHandler(error);
                }
            }
        }
    };

    const headerClick = (button, name) => {
        console.log(`Button: ${button}, Name: ${name} `);

        if (button === 'asc' || button === 'desc') {
            setSortColumn(name);
            setSortOrder(button);
            setCurrentItemNumber(0);


        } else if (button === 'filter') {
            setTempFilteringColumns({ ...filteringColumns });
            setFilterColumn(name);
            setShowFilterDialog(true);
            setCurrentItemNumber(0);

        }
    }


    const formatDate = (date) => {
        return date.toLocaleDateString();
    }


    const renderCellValue = (info, row, value, keyid) => {
        const maxDisplayWidth = info.max_disp_chars;

        let displayValue = value;
        let tooltip: string | null = null;

        switch (info.type) {
            case 'url':
                if (value && value.startsWith("http")) {
                    return <a href={value} target="_blank" rel="noopener noreferrer">Open Link</a>;
                }
                break;

            case 'date':
                // Convert ISO date string to local date format
                if (!(is_null_or_empty(value))) {
                    displayValue = formatDate(new Date(value));

                }
                break;

            case 'action':
                const action = info.action;

                return <Button
                    size='small'
                    disabled={info.disabled_if != null && info.disabled_if(row)}
                    key={row['id'] + action}
                    id={action + row['id']}
                    variant="outlined" onClick={() => handleActionClick(keyid, row, info)}>
                    <span style={{ 'whiteSpace': 'nowrap' }}>{action}</span>
                </Button>

            case 'boolean':
                if (typeof value === 'boolean') {
                    displayValue = value ? 'true' : 'false';
                } else {
                    if (value === '0') {
                        displayValue = 'false';
                    } else if (value === '1') {
                        displayValue = 'true';
                    } else {
                        displayValue = value;
                    }

                }
                break;

            default:
                // Truncate long text if needed
                if (typeof value === 'string' && value.length > maxDisplayWidth) {
                    displayValue = `${value.substring(0, maxDisplayWidth)}...`;
                    tooltip = value;

                } else if (typeof value === 'boolean') {
                    displayValue = `${value}`;
                    tooltip = displayValue;
                }
                break;
        }

        if (!info.wrap) {
            displayValue = <span style={{ 'whiteSpace': 'nowrap' }}>{displayValue}</span>;
        }

        if (tooltip) {
            displayValue = <Tooltip title={tooltip}>{displayValue}</Tooltip>;
        }

        return displayValue;
    };

    const handleToggleSort = (columnId) => {
        if (sortColumnn === columnId) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(columnId);
            setSortOrder('asc');
        }
    }



    const renderHeader = () => {
        return (
            <thead>
                <tr key='headerrow'>
                    {columns && columns.map(column => (
                        <Fragment key={column.id + 'headerfragment'}>
                            {shouldShow(column) && <th id={column.id + 'header'} key={column.id + 'header'} className={column.freeze ? "sticky-col first-col" : ""}  >

                                {column.type !== 'action' &&

                                    <div style={{ "display": "flex" }} >
                                        <TableColmnHeader key={column.id + 'theader'}
                                            onClickMenuItem={headerClick}
                                            columnId={column.id}
                                            name={column.disp_name} />

                                        {sortColumnn === column.id &&
                                            <span>{sortOrder === 'asc' ? <ArrowUpwardIcon style={{ 'fontSize': 'small' }} /> : <ArrowDownwardIcon style={{ 'fontSize': 'small' }} />}</span>
                                        }

                                    </div>

                                }
                            </th >
                            }
                        </Fragment>
                    ))}

                </tr>
            </thead >
        );
    };

    const shouldShow = (column) => {
        if (column.hidden) {
            return false;
        }

        if (column.show_in_edit_only === undefined) {
            return true;
        }

        return !column.show_in_edit_only;
    }

    const renderRow = (row) => {
        return (
            <tr key={row['id'] + 'row'} id={row['id']}  >
                {columns && columns.map(column => (
                    shouldShow(column) && (
                        <td key={row['id'] + column.id}
                            style={{ 'minWidth': column.disp_width, 'textWrap': 'pretty' }} >

                            {renderCellValue(column, row, row[column.id], row[keyname])}
                        </td>
                    )
                ))
                }
            </tr >
        );
    };

    const handleExportFiltered = () => {
        csvexporter.exportToCsv('export.csv', columns, sortedAndFilteredData);
    }


    const handleUserSetFilter = () => {
        setFilterValue(null);
        setFilteringColumns({ ...tempFilteringColumns });
    }

    const handleDeleteFilterForColumn = (column) => {
        const filteringColumnsCopy = { ...filteringColumns };
        delete filteringColumnsCopy[column];
        setFilteringColumns(filteringColumnsCopy);
    }

    return (
        <>
            <div style={{ "display": "flex", "alignItems": "center", "flexDirection": "row", "paddingBottom": "10px" }}>

                {data.length > 0 &&
                    <div  >

                        {showExport &&

                            <Button
                                style={{ "marginRight": "10px" }}
                                variant="outlined" color="primary" onClick={handleExportFiltered} >Export</Button>
                        }

                        <TextField
                            id="search"

                            label="Search"
                            variant="outlined"
                            size="small"
                            onChange={(e) => {
                                setSearchValue(e.target.value);
                                setCurrentItemNumber(0);
                            }}
                        />
                    </div>
                }
                <div style={{ "paddingLeft": "10px" }} >
                    {children}
                </div>

                <FilterChips
                    columns={columns} filteringColumns={filteringColumns} deleteFilterForColumn={handleDeleteFilterForColumn} />

            </div>

            <Typography variant="caption" display="block"   >
                <div className="view">
                    <div className="wrapper">
                        <table
                            id="data-table"
                            className="data-table table">

                            {renderHeader()}
                            <tbody>
                                {currentPageData && currentPageData.map(row => renderRow(row))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Typography>

            <br />
            <div style={{ "display": "flex", }}>
                <div>
                    {data.length > pageSize &&
                        <TablePagination
                            color="primary"
                            count={sortedAndFilteredData.length}
                            page={pageNum}
                            onPageChange={handleChangePage}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 20, 50, 100, 1000]}
                        />
                    }
                </div>


            </div>


            <FilterDialog columns={columns}
                showFilterDialog={showFilterDialog}
                setShowFilterDialog={setShowFilterDialog}
                filterColumn={filterColumn}
                currentFilterValue={tempFilteringColumns[filterColumn ?? '']} //TODO: fix this
                updatedFilterValue={addFilter}
                userSetFilter={handleUserSetFilter}
            />



        </>
    )
}


export const widthStyleDataEntry = { "width": "400px" }
const refresh_action_id = 'refresh';

const is_null_or_empty = (value) => {
    return value == null || value === '' || String(value).trim() === '';
}

const is_null_or_empty_or_value = (value, is_value) => {
    return is_null_or_empty(value) || value === is_value;
}


const CutsomStyledButton = styled(Button)({
    textTransform: 'none',
});


function TableColmnHeader({ name, columnId, onClickMenuItem }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (button, name) => {
        if (onClickMenuItem) {
            onClickMenuItem(button, columnId);
        }

        setAnchorEl(null);
    };


    return <>
        <CutsomStyledButton
            style={{ 'minWidth': '10px', 'fontSize': '0.1', 'padding': '0px' }}
            onClick={(e) => { e.preventDefault(); handleClick(e); return false; }} size='small' variant="text"> <MenuIcon
                style={{ 'fontSize': 'medium' }}
            />  </CutsomStyledButton>

        {name}

        <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
                'aria-labelledby': 'basic-button',
            }}
        >

            <MenuItem onClick={() => handleClose('asc', name)}>Ascending</MenuItem>
            <MenuItem onClick={() => handleClose('desc', name)}>Descending</MenuItem>
            <MenuItem onClick={() => handleClose('filter', name)}>Filter</MenuItem>
        </Menu>
    </>

}

const defaultSortColumn = 'record_num';

const applySort = (columnsInfo, data, column, order) => {
    if (!column) {
        return data;
    }

    const columnInfo = columnsInfo.find(col => col.id === column);

    //just compare strings by default, ignore sort order 
    var defaultCompare = (a, b) => {
        if (a[column] < b[column]) {
            return -1;
        }
        if (a[column] > b[column]) {
            return 1;
        }
        return 0;
    }

    if (columnInfo.sortFunction) {
        defaultCompare = columnInfo.sortFunction;
    }


    const sortedData = [...data].sort((a, b) => {

        return order === 'asc' ? defaultCompare(a, b) : defaultCompare(b, a);
    });

    return sortedData;
};

const sortAndFilter = (status, searchValue, columns, datToFilter, sortColumnn, sortOrder, filterHsh) => {
    const sortedFilteredData = applySort(columns, datToFilter, sortColumnn, sortOrder);

    const filteredData = sortedFilteredData.filter(row => {
        for (const [filterColumn, filterValue] of Object.entries(filterHsh)) {
            const val = filterValue ?? ''; //TODO: fix this
            const filterValueString = val.toString().toLowerCase();

            if (filterValueString && filterValueString.length > 0) {
                const value = row[filterColumn];
                if (value) {
                    if (!(value.toString().toLowerCase().includes(filterValueString.toLowerCase()))) {
                        return false;
                    }
                } else {
                    return false;
                }
            }
        }

        //if there is a search value, check if any column contains the search value
        if (searchValue && searchValue.length > 0) {
            var found = false;
            for (const column of columns) {

                const value = row[column.id];
                if (value && value.toString().toLowerCase().includes(searchValue.toLowerCase())) {
                    found = true;
                    break;
                }
            }

            if (!found) {
                return false;
            }
        }

        return true;
    });

    return filteredData;
}

export function getFilterDispName(columns, columnId) {
    const column = columns.find(column => column.id === columnId);
    return column ? column.disp_name : columnId;
}

export default DataTable;



